import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { FButton } from "@/components/forkd/FButton";
import { FInput } from "@/components/forkd/FInput";
import { Wordmark } from "@/components/forkd/Wordmark";
import { onboarding } from "@/lib/onboarding";

export default function SignIn() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      setErr(error.message);
      return;
    }
    nav(onboarding.isComplete() ? "/home" : "/onboarding", { replace: true });
  };

  const onGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });
    if (error) toast.error(error.message);
  };

  return (
    <div className="min-h-screen bg-[var(--colour-background)] px-5 pt-16 pb-10 flex flex-col">
      <div className="flex justify-center mb-10">
        <Wordmark size={44} />
      </div>

      <div className="space-y-3 mb-6">
        <FButton variant="outline" fullWidth onClick={onGoogle} type="button">
          Sign in with Google
        </FButton>
        <FButton
          variant="outline"
          fullWidth
          disabled
          title="Coming soon"
          type="button"
        >
          Sign in with Apple
        </FButton>
        <p className="text-center text-[12px] text-[var(--colour-text-secondary)]">
          Apple Sign-In coming soon
        </p>
      </div>

      <div className="flex items-center gap-3 my-2">
        <div className="flex-1 h-px bg-[var(--colour-border)]" />
        <span className="text-[12px] text-[var(--colour-text-secondary)]">or</span>
        <div className="flex-1 h-px bg-[var(--colour-border)]" />
      </div>

      <form onSubmit={onSubmit} className="space-y-3 mt-4">
        <FInput
          name="email"
          type="email"
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
        />
        <FInput
          name="password"
          type="password"
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          error={err}
        />
        <FButton type="submit" fullWidth loading={loading}>
          Sign In
        </FButton>
      </form>

      <div className="mt-auto pt-8 text-center space-y-3">
        <Link
          to="/forgot-password"
          className="block text-[14px] text-[var(--colour-text-secondary)]"
        >
          Forgot password?
        </Link>
        <p className="text-[14px] text-[var(--colour-text-secondary)]">
          Don't have an account?{" "}
          <Link to="/signup" className="text-[var(--colour-accent)] font-semibold">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
